/**
 * ============================================================
 *  SCROLL OBSERVER + SPRING PHYSICS v1.0 — Pro Chef Israel
 * ============================================================
 *  - IntersectionObserver for scroll-triggered [data-animate]
 *  - Stagger delay for child elements
 *  - Lightweight spring physics for keychain pendulum
 *  - Floating SVG particle manager
 *  - Reduced motion detection
 * ============================================================
 */

(function () {
  'use strict';

  /* ────────────────────────────────────────────────
   * 0. REDUCED MOTION CHECK
   * ──────────────────────────────────────────────── */
  const prefersReducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ────────────────────────────────────────────────
   * 1. SCROLL-TRIGGERED ANIMATIONS
   * ──────────────────────────────────────────────── */
  function initScrollObserver() {
    const elements = document.querySelectorAll('[data-animate]');
    if (!elements.length) return;

    // If reduced motion, reveal everything immediately
    if (prefersReducedMotion) {
      elements.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          var el = entry.target;
          var delay = el.dataset.animateDelay;

          if (delay) {
            el.style.transitionDelay = delay;
          }

          // Stagger children
          if (el.dataset.animate === 'stagger') {
            var children = el.querySelectorAll('[data-animate-child]');
            var staggerMs = parseFloat(
              getComputedStyle(document.documentElement)
                .getPropertyValue('--anim-stagger')
            ) || 0.08;

            children.forEach(function (child, i) {
              child.style.transitionDelay = (i * staggerMs) + 's';
            });
          }

          el.classList.add('is-visible');
          observer.unobserve(el); // animate once
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    elements.forEach(function (el) { observer.observe(el); });
  }


  /* ────────────────────────────────────────────────
   * 2. SPRING PHYSICS ENGINE (lightweight)
   * ──────────────────────────────────────────────── */

  /**
   * Simple damped harmonic oscillator.
   * Returns a function that, given time t (seconds),
   * returns the angular displacement in degrees.
   *
   * @param {Object} opts
   * @param {number} opts.angle0      Initial angle in degrees (default 15)
   * @param {number} opts.stiffness   Spring stiffness (default 120)
   * @param {number} opts.damping     Damping ratio 0-1+ (default 0.35)
   * @param {number} opts.mass        Mass (default 1)
   */
  function createSpring(opts) {
    var angle0    = (opts && opts.angle0)    || 15;
    var stiffness = (opts && opts.stiffness) || 120;
    var damping   = (opts && opts.damping)   || 0.35;
    var mass      = (opts && opts.mass)      || 1;

    var omega = Math.sqrt(stiffness / mass);         // natural frequency
    var gamma = damping * 2 * Math.sqrt(stiffness * mass); // damping coefficient
    var halfGamma = gamma / (2 * mass);

    // Under-damped case (most visually interesting)
    var omegaD = Math.sqrt(Math.max(omega * omega - halfGamma * halfGamma, 0.01));

    return function (t) {
      return angle0 * Math.exp(-halfGamma * t) * Math.cos(omegaD * t);
    };
  }


  /* ────────────────────────────────────────────────
   * 3. KEYCHAIN PENDULUM CONTROLLER
   * ──────────────────────────────────────────────── */
  function initPendulums() {
    var pendulums = document.querySelectorAll('[data-pendulum]');
    if (!pendulums.length) {
      /* Fallback: also check old attribute for backwards compat */
      pendulums = document.querySelectorAll('.keychain-pendulum[data-spring]');
    }
    if (!pendulums.length) return;

    if (prefersReducedMotion) {
      pendulums.forEach(function (el) {
        el.style.transform = 'rotate(0deg)';
      });
      return;
    }

    pendulums.forEach(function (el) {
      var config = {};
      /* Support both old JSON data-spring and new individual data attributes */
      if (el.dataset.spring) {
        try { config = JSON.parse(el.dataset.spring); } catch (e) { config = {}; }
      } else {
        config.angle = parseFloat(el.dataset.pendulumAngle) || 15;
        config.stiffness = parseFloat(el.dataset.pendulumStiffness) || 120;
        config.damping = parseFloat(el.dataset.pendulumDamping) || 0.35;
      }

      var spring = createSpring({
        angle0:    config.angle    || 15,
        stiffness: config.stiffness || 120,
        damping:   config.damping   || 0.35,
        mass:      config.mass      || 1
      });

      // Use IntersectionObserver to start animation only when visible
      var pendulumObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            animatePendulum(el, spring);
            pendulumObserver.unobserve(el);
          });
        },
        { threshold: 0.2 }
      );

      pendulumObserver.observe(el);
    });
  }

  function animatePendulum(el, spring) {
    var start = null;
    var duration = 4000; // 4 seconds of physics simulation
    var insideMount = true; /* v4: always simple rotate, no translateX */

    function tick(timestamp) {
      if (!start) start = timestamp;
      var elapsed = (timestamp - start) / 1000; // seconds

      if (elapsed > duration / 1000) {
        el.style.transform = insideMount
          ? 'rotate(0deg)'
          : 'translateX(-50%) rotate(0deg)';
        return;
      }

      var angle = spring(elapsed);
      el.style.transform = insideMount
        ? 'rotate(' + angle.toFixed(2) + 'deg)'
        : 'translateX(-50%) rotate(' + angle.toFixed(2) + 'deg)';
      requestAnimationFrame(tick);
    }

    requestAnimationFrame(tick);
  }


  /* Chain link system removed in v4 — using simple pendulum instead */


  /* ────────────────────────────────────────────────
   * 4. FLOATING PARTICLE SYSTEM
   * ──────────────────────────────────────────────── */

  /**
   * SVG icon templates per category.
   * Rendered as crisp SVGs, NOT emoji.
   */
  var PARTICLE_ICONS = {
    satoshi: [
      // Knife
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 21l9-9m0 0l3-3m-3 3h-3m6 0V9m-3 3l9-9"/><path d="M14 4l2-2 6 6-2 2"/></svg>',
      // Blade sparkle
      '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.09 6.26L20.18 10l-6.09 1.74L12 18l-2.09-6.26L3.82 10l6.09-1.74z"/></svg>',
      // Sparkle small
      '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 1l1.5 4.5L18 7l-4.5 1.5L12 13l-1.5-4.5L6 7l4.5-1.5z"/></svg>'
    ],
    chef: [
      // Chef hat
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6 20h12M7 16v4M17 16v4"/><path d="M7 16c-2.5 0-4-2-4-4.5S5 7 8 7c0-2.5 2-4 4-4s4 1.5 4 4c3 0 5 2.5 5 4.5S19.5 16 17 16H7z"/></svg>',
      // Star
      '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z"/></svg>',
      // Plate
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><ellipse cx="12" cy="14" rx="9" ry="5"/><path d="M3 14c0-3.5 4-7 9-7s9 3.5 9 7"/></svg>'
    ],
    smoke: [
      // Flame
      '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 23c-4.97 0-9-3.58-9-8 0-4 3.5-7 5-9 .5 3 2.5 5 4 5 0-4 2-7 4-9 2 3 4 6 4 9v1c0 6-4 11-8 11z"/></svg>',
      // Smoke wisps
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M4 16c0-2 2-3 2-5s-1-3-1-5"/><path d="M12 16c0-2 2-3 2-5s-1-3-1-5"/><path d="M20 16c0-2 2-3 2-5s-1-3-1-5"/></svg>',
      // Glass
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M8 2l-1 9c0 3 2.5 5 5 5s5-2 5-5l-1-9H8z"/><line x1="12" y1="16" x2="12" y2="22"/><line x1="8" y1="22" x2="16" y2="22"/></svg>'
    ],
    hot: [
      // Fire
      '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 23c-4.97 0-9-3.58-9-8 0-4 3.5-7 5-9 .5 3 2.5 5 4 5 0-4 2-7 4-9 2 3 4 6 4 9v1c0 6-4 11-8 11z"/></svg>',
      // Lightning
      '<svg viewBox="0 0 24 24" fill="currentColor"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10"/></svg>',
      // Star
      '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z"/></svg>'
    ]
  };

  /**
   * Spawns SVG particles inside a container.
   * @param {HTMLElement} container  - the .particles-container element
   * @param {string}      category  - 'satoshi' | 'chef' | 'smoke' | 'hot'
   * @param {number}      count     - number of particles (default 12)
   */
  function spawnParticles(container, category, count) {
    if (prefersReducedMotion) return;
    if (!container || !PARTICLE_ICONS[category]) return;

    count = count || 12;
    var icons = PARTICLE_ICONS[category];
    var sizes = ['particle--sm', 'particle--md', 'particle--lg'];

    for (var i = 0; i < count; i++) {
      var particle = document.createElement('div');
      var iconIdx = i % icons.length;
      var sizeIdx = Math.floor(Math.random() * sizes.length);

      particle.className = 'particle particle--active particle--' + category + ' ' + sizes[sizeIdx];
      particle.innerHTML = icons[iconIdx];
      particle.setAttribute('aria-hidden', 'true');

      // Randomize position and animation properties
      var left     = (Math.random() * 90 + 5).toFixed(1);
      var bottom   = (Math.random() * 30).toFixed(1);
      var travel   = -(200 + Math.random() * 250).toFixed(0);
      var drift    = (Math.random() * 60 - 30).toFixed(0);
      var rotate   = (Math.random() * 120 - 60).toFixed(0);
      var opacity  = (0.2 + Math.random() * 0.35).toFixed(2);
      var duration = (6 + Math.random() * 8).toFixed(1);
      var delay    = (Math.random() * 10).toFixed(1);

      particle.style.cssText =
        'left:' + left + '%;' +
        'bottom:' + bottom + '%;' +
        '--particle-travel:' + travel + 'px;' +
        '--particle-drift:' + drift + 'px;' +
        '--particle-rotate:' + rotate + 'deg;' +
        '--particle-opacity:' + opacity + ';' +
        '--particle-duration:' + duration + 's;' +
        '--particle-delay:' + delay + 's;' +
        '--particle-color:rgba(var(--cat-' + category + '-primary-rgb),0.3);';

      container.appendChild(particle);
    }
  }

  // Expose spawnParticles globally so sections can call it
  window.ProChefAnimations = window.ProChefAnimations || {};
  window.ProChefAnimations.spawnParticles = spawnParticles;
  window.ProChefAnimations.createSpring = createSpring;
  window.ProChefAnimations.initPendulums = initPendulums;
  /* initChainLinks removed in v4 */


  /* ────────────────────────────────────────────────
   * 5. AUTO-INIT PARTICLES FROM DATA ATTRIBUTES
   * ──────────────────────────────────────────────── */
  function initParticles() {
    var containers = document.querySelectorAll('.particles-container[data-category]');
    containers.forEach(function (container) {
      var category = container.dataset.category;
      var count    = parseInt(container.dataset.particleCount, 10) || 12;
      spawnParticles(container, category, count);
    });
  }


  /* ────────────────────────────────────────────────
   * 6. 3D TEXT MOUSE TILT
   * ──────────────────────────────────────────────── */
  function init3DTextHover() {
    var elements = document.querySelectorAll('.text-3d[data-hover-3d]');
    if (prefersReducedMotion || !elements.length) return;

    elements.forEach(function (el) {
      el.addEventListener('mousemove', function (e) {
        var rect = el.getBoundingClientRect();
        var x = (e.clientX - rect.left) / rect.width - 0.5;
        var y = (e.clientY - rect.top) / rect.height - 0.5;

        el.style.transform =
          'rotateX(' + (-y * 8).toFixed(1) + 'deg) ' +
          'rotateY(' + (x * 8).toFixed(1) + 'deg) ' +
          'translateZ(10px)';
      });

      el.addEventListener('mouseleave', function () {
        el.style.transform = '';
      });
    });
  }


  /* ────────────────────────────────────────────────
   * 7. BOOT
   * ──────────────────────────────────────────────── */
  function boot() {
    initScrollObserver();
    initPendulums();
    /* initChainLinks removed in v4 */
    initParticles();
    init3DTextHover();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Also re-init on Shopify section render (Theme Editor support)
  document.addEventListener('shopify:section:load', function () {
    initScrollObserver();
    initPendulums();
    /* initChainLinks removed in v4 */
    initParticles();
    init3DTextHover();
  });
})();
